import { EntityConfig } from './types';
import { Badge } from '@/components/ui/badge';
import { Building2 } from 'lucide-react';
import { format } from 'date-fns';

// Client entity type matching the database schema
export interface Client {
  id: string;
  name: string;
  company_name: string | null;
  category: 'services' | 'staffing' | 'end_client' | 'unknown' | null;
  is_active: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string | null;
}

// Category options for filters and forms
const CATEGORY_OPTIONS = [
  { label: 'Services', value: 'services' },
  { label: 'Staffing', value: 'staffing' },
  { label: 'End Client', value: 'end_client' },
  { label: 'Unknown', value: 'unknown' },
];

// Status options for filters
const STATUS_OPTIONS = [
  { label: 'All Status', value: 'all' },
  { label: 'Active', value: 'true' },
  { label: 'Inactive', value: 'false' },
];

// Category filter options (with "all" option)
const CATEGORY_FILTER_OPTIONS = [
  { label: 'All Categories', value: 'all' },
  ...CATEGORY_OPTIONS,
];

export const clientsConfig: EntityConfig<Client> = {
  // Metadata
  name: 'clients',
  singularName: 'Client',
  pluralName: 'Clients',
  tableName: 'clients',
  keyField: 'id',
  icon: <Building2 className="h-5 w-5" />,
  description: 'Manage client accounts and relationships',
  
  // Permissions
  permissionModule: 'clients',
  
  // Data Configuration
  defaultSelect: '*',
  defaultOrderBy: { field: 'created_at', ascending: false },
  searchFields: ['name', 'company_name'],
  defaultPageSize: 25,
  
  // Table Columns
  columns: [
    {
      id: 'name',
      header: 'Name',
      accessorKey: 'name',
      sortable: true,
    },
    {
      id: 'company_name',
      header: 'Company',
      accessorKey: 'company_name',
      cell: (row) => row.company_name || '-',
      sortable: true,
    },
    {
      id: 'category',
      header: 'Category',
      accessorKey: 'category',
      cell: (row) => (
        <Badge variant="outline" className="capitalize">
          {row.category?.replace('_', ' ') || 'Unknown'}
        </Badge>
      ),
      sortable: true,
    },
    {
      id: 'is_active',
      header: 'Status',
      accessorKey: 'is_active',
      cell: (row) => (
        <Badge variant={row.is_active ? 'default' : 'secondary'}>
          {row.is_active ? 'Active' : 'Inactive'}
        </Badge>
      ),
      sortable: true,
    },
    {
      id: 'created_at',
      header: 'Created',
      accessorKey: 'created_at',
      cell: (row) => format(new Date(row.created_at), 'MMM d, yyyy'),
      sortable: true,
      defaultVisible: false,
    },
  ],
  
  // Form Fields
  formFields: [
    {
      name: 'name',
      label: 'Client Name',
      type: 'text',
      required: true,
      placeholder: 'Enter client name',
      gridSpan: 1,
    },
    {
      name: 'company_name',
      label: 'Company Name',
      type: 'text',
      placeholder: 'Enter company name',
      gridSpan: 1,
    },
    {
      name: 'category',
      label: 'Category',
      type: 'select',
      options: CATEGORY_OPTIONS,
      defaultValue: 'unknown',
      gridSpan: 1,
    },
    {
      name: 'is_active',
      label: 'Active',
      type: 'switch',
      defaultValue: true,
      helpText: 'Inactive clients will not appear in job assignment dropdowns',
      gridSpan: 1,
    },
    {
      name: 'notes',
      label: 'Notes',
      type: 'textarea',
      placeholder: 'Add any relevant notes about this client...',
      gridSpan: 2,
    },
  ],
  
  // Filters
  filters: [
    {
      id: 'status',
      label: 'Status',
      field: 'is_active',
      type: 'select',
      options: STATUS_OPTIONS,
      defaultValue: 'all',
    },
    {
      id: 'category',
      label: 'Category',
      field: 'category',
      type: 'select',
      options: CATEGORY_FILTER_OPTIONS,
      defaultValue: 'all',
    },
  ],
  
  // Audit Configuration
  audit: {
    enabled: true,
    excludeFields: ['updated_at'],
    detailedChangelog: true,
  },
  
  // Notification Configuration
  notifications: {
    onCreate: {
      eventType: 'client_created',
      recipients: 'admins',
    },
    onUpdate: {
      eventType: 'client_updated',
      recipients: 'admins',
    },
    onDelete: {
      eventType: 'client_deleted',
      recipients: 'admins',
    },
  },
  
  // Routes
  routes: {
    list: '/recruiter/clients',
    detail: '/recruiter/clients/:id',
  },
  
  // Feature Flags
  features: {
    canCreate: true,
    canUpdate: true,
    canDelete: true,
    canBulkDelete: true,
    canExport: true,
    canSearch: true,
    showActivityLog: true,
  },
};
